import React, { FC, useState } from "react";
import { useStripe, useElements, CardElement } from "@stripe/react-stripe-js";
import { StripeCardElement } from "@stripe/stripe-js";
import firebase from "firebase/app";
import { LabelWrapper, StripeInput, Form, Button, Dialog } from "../System";
import { StyledPaymentModal } from ".";
import { useNotifications } from "../Notifications";

type Props = {
  show: boolean;
  setShow: (state: boolean) => void;
};

const PaymentModal: FC<Props> = ({ show, setShow }: Props) => {
  const stripe = useStripe();
  const elements = useElements();
  const { addNotification } = useNotifications();

  const attachPaymentMethod = firebase
    .functions()
    .httpsCallable("attachPaymentMethod");

  const [cardError, setCardError] = useState("");
  const [isLoading, setIsLoading] = useState(false);
  const [isComplete, setIsComplete] = useState(false);

  const handleChange = (e) => {
    setCardError("");
    setIsComplete(e.complete);

    if (e.error?.message) {
      setCardError(e.error.message);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsLoading(true);
    if (!stripe || !elements) {
      setIsLoading(false);
      return;
    }

    const cardElement = elements.getElement(CardElement);

    const { error, paymentMethod } = await stripe.createPaymentMethod({
      type: "card",
      card: cardElement as StripeCardElement,
    });

    if (error) {
      setCardError(error.message as string);
      setIsLoading(false);
      return;
    }

    const method = await attachPaymentMethod({
      paymentMethodId: paymentMethod?.id,
    }).catch((err) => {
      setIsLoading(false);
      setCardError(err.message);
    });

    if (!method) {
      setIsLoading(false);
      return;
    }
    setShow(false);
    setIsLoading(false);
    addNotification({
      message: "Payment method saved!",
      type: "success",
      interval: 5000,
    });
  };

  return (
    <Dialog isOpen={show} onClose={() => setShow(false)}>
      <StyledPaymentModal>
        <h1>Update Card Information</h1>
        <Form onSubmit={(e) => handleSubmit(e)}>
          <LabelWrapper errorMessage={cardError}>
            <StripeInput onChange={(e) => handleChange(e)} />
          </LabelWrapper>
          <Button
            isDisabled={!isComplete || cardError || isLoading}
            isLoading={isLoading}
            action="submit"
          >
            Update
          </Button>
        </Form>
      </StyledPaymentModal>
    </Dialog>
  );
};

export default PaymentModal;
